import { GUID } from "@faro-lotv/foundation";
import { Vector3 } from "three";
import {
  buildSegments,
  constrainTranslation,
  createGuidanceOutput,
  createTunnelPoint,
  evaluateTunnel,
  GuidanceOutput,
  SEGMENT_STRIDE,
  SplatHit,
} from "./guided-navigation-math";
import { GUIDED_NAVIGATION_PARAMS as P } from "./guided-navigation-params";

/** Squared translation below which the camera is considered still */
const MOVEMENT_EPSILON_SQ = 1e-8;

/** Vertical translation below which the camera is not considered moving vertically */
const VERTICAL_MOVEMENT_EPSILON = 1e-4;

/** @returns the current time in seconds */
export function nowSeconds(): number {
  return performance.now() / 1000;
}

/**
 * Mutable state of the guided navigation of a single view. It is updated every frame,
 * so it is not stored in React state to avoid re-renders.
 */
export class GuidedNavigationState {
  #polylines = new Map<GUID, Vector3[]>();

  #tunnel = createTunnelPoint();

  #segments = new Float32Array(0);

  /** Incremented whenever the trajectory segments change */
  segmentsVersion = 0;

  /** Result of the last raycast on the Gaussian Splats along the horizontal movement direction */
  readonly splatHit: SplatHit = {
    valid: false,
    distance: 0,
    origin: new Vector3(),
    direction: new Vector3(),
  };

  /** Result of the last raycast on the Gaussian Splats above or below the camera */
  readonly verticalSplatHit: SplatHit = {
    valid: false,
    distance: 0,
    origin: new Vector3(),
    direction: new Vector3(),
  };

  /** Sign of the last vertical translation of the camera: 1 up, -1 down, 0 none */
  verticalDirection = 0;

  /** Normalized horizontal direction of the last translation of the camera, zero if there was none */
  readonly moveDirection = new Vector3();

  /** Information about the boundaries close to the camera */
  readonly output: GuidanceOutput = createGuidanceOutput();

  /** Last time, in seconds, the camera translated */
  lastMoveTime = -Infinity;

  /** Last time, in seconds, a translation of the camera was reduced by the boundaries */
  lastConstrainedTime = -Infinity;

  /** Last time, in seconds, the camera translated towards a grid wall or was constrained */
  lastApproachTime = -Infinity;

  /** Last time, in seconds, the user clicked a point that is not allowed */
  lastDeniedClickTime = -Infinity;

  /**
   * @param id id of the path
   * @param points trajectory of the path at the acquisition device positions, undefined to remove it
   */
  setTrajectory(id: GUID, points: Vector3[] | undefined): void {
    if (points && points.length > 1) {
      this.#polylines.set(id, points);
    } else if (!this.#polylines.delete(id)) {
      return;
    }
    this.#segments = buildSegments(this.#polylines.values());
    this.segmentsVersion++;
  }

  /**
   * @returns the trajectory segments with a point within P.corridorDisplayRange vertically from the camera,
   * see SEGMENT_STRIDE
   * @param cameraY elevation of the camera
   */
  getCorridorSegments(cameraY: number): Float32Array {
    const segments = this.#segments;
    const values: number[] = [];
    for (let o = 0; o < segments.length; o += SEGMENT_STRIDE) {
      const y0 = segments[o + 1];
      const y1 = segments[o + 4];
      if (
        Math.min(y0, y1) - P.corridorDisplayRange <= cameraY &&
        cameraY <= Math.max(y0, y1) + P.corridorDisplayRange
      ) {
        values.push(...segments.subarray(o, o + SEGMENT_STRIDE));
      }
    }
    return new Float32Array(values);
  }

  /**
   * @returns true if the camera translated recently enough to show the guides
   * @param now current time in seconds
   */
  isMoving(now: number = nowSeconds()): boolean {
    return now - this.lastMoveTime < P.fadeOutDelay;
  }

  /**
   * @returns true if the corridor floor should be visible: the movement is being constrained
   * or the user recently clicked a point that is not allowed
   * @param now current time in seconds
   */
  shouldShowCorridor(now: number = nowSeconds()): boolean {
    return (
      now - this.lastConstrainedTime < P.constraintHoldTime ||
      now - this.lastDeniedClickTime < P.deniedClickShowTime
    );
  }

  /**
   * @returns true if the grid walls should be visible: the camera is translating towards a wall or is constrained.
   * They are not while it is still or only rotating.
   * @param now current time in seconds
   */
  shouldShowWalls(now: number = nowSeconds()): boolean {
    return now - this.lastApproachTime < P.approachHoldTime;
  }

  /**
   * Moves a position outside of the trajectory tunnels to the closest point inside them
   *
   * @param position the position, modified in place. Left unchanged without trajectories.
   */
  clampToTunnel(position: Vector3): void {
    const tunnel = evaluateTunnel(this.#segments, position, this.#tunnel);
    if (!tunnel.found || tunnel.depth >= 0) return;
    position
      .copy(tunnel.boundaryPoint)
      .addScaledVector(tunnel.normal, -P.clickInset);
    this.lastDeniedClickTime = nowSeconds();
  }

  /**
   * Constrains a camera translation, see constrainTranslation
   *
   * @param from camera position before the translation
   * @param to camera position after the translation, modified in place
   * @param isRotation whether the translation is only due to the camera rotating around the controls pivot.
   * It is still constrained, but it does not show the guides.
   */
  readonly constrain = (
    from: Readonly<Vector3>,
    to: Vector3,
    isRotation = false,
  ): void => {
    if (from.distanceToSquared(to) < MOVEMENT_EPSILON_SQ || isRotation) {
      this.verticalDirection = 0;
      return;
    }
    const dy = to.y - from.y;
    this.verticalDirection =
      Math.abs(dy) > VERTICAL_MOVEMENT_EPSILON ? Math.sign(dy) : 0;
    this.moveDirection.set(to.x - from.x, 0, to.z - from.z).normalize();
    constrainTranslation(
      from,
      to,
      this.#segments,
      this.splatHit,
      this.verticalSplatHit,
      this.#tunnel,
      this.output,
    );
    this.lastMoveTime = nowSeconds();
    if (this.output.isConstrained) this.lastConstrainedTime = this.lastMoveTime;
    if (this.output.isConstrained || this.output.isApproaching) {
      this.lastApproachTime = this.lastMoveTime;
    }
  };
}
